import React from 'react';
import { compose, QueryResult } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouterProps } from 'react-router';
import { createCouponVariables, updateCouponVariables, CouponType, CouponAppliedTo, AdminShippingMethodsList_adminShippingMethods, AdminPaymentMethodsList_adminPaymentMethods, AdminShippingMethodsList, AdminPaymentMethodsList } from '@api/graphql/types';
import * as Queries from 'api/graphql/queries';
import CouponCodeAttributesForm from './CouponCodeAttributesForm';
import PriceRangesField from './PriceRangesField';
import CouponCodesField, { CodeCodeUsageCount } from './CouponCodesField';
import ProductInput, { ProductIdName } from './ProductInput';
import { Loading } from '@components/Loading';
import Paths from '@root/utils/Path';
import Form from '@components/Form/Form';

interface Props {
    formData: createCouponVariables | updateCouponVariables;
    codes: CodeCodeUsageCount[];
    products: ProductIdName[];
    originalMaxUsageCount: number;
    generatedCodesCount: number | null;
    onSubmit: (formData: createCouponVariables | updateCouponVariables, codes: CodeCodeUsageCount[], products: ProductIdName[]) => Promise<any>;
}

type PropType = Props & InjectedIntlProps & RouterProps;

interface State {
    formData: createCouponVariables | updateCouponVariables;
    codes: CodeCodeUsageCount[];
    products: ProductIdName[];
    originalMaxUsageCount: number;
    generatedCodesCount: number | null;
}

class CouponForm extends React.Component<PropType, State> {
    public constructor(props: PropType) {
        super(props);
        this.state = {
            formData: this.props.formData,
            codes: this.props.codes || [],
            products: this.props.products || [],
            originalMaxUsageCount: this.props.formData.usage_count,
            generatedCodesCount: this.props.generatedCodesCount,
        };
    }

    private validate = () => {
        return true;
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.coupon.${id}` });
    }

    private onChange = (value: any, name: keyof createCouponVariables) => {
        if (name === "products") {
            this.setState({ products: value });
         } else if (name === "codes") {
            this.setState({ codes: value });
         } else {
            this.setState({ formData: { ...this.state.formData, [name]: value } });
         }
    }

    private onFormChange = (formData: createCouponVariables | updateCouponVariables) => {
        this.setState({formData});
    }

    private onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        return this.props.onSubmit(this.state.formData, this.state.codes, this.state.products);
    }

    public render() {
        if (!this.state) {
            return <Loading />;
        }

        const fields = [
            {
                id: 'default',
                fields: [
                    {name: 'name', type: 'text', props: {required: true}},
                    {name: 'description', type: 'textarea'},
                    {name: 'active_from', type: 'datetime', props: {required: true}},
                    {name: 'active_to', type: 'datetime', props: {required: true}},

                    {name: 'type', type: 'select', props: {options: Object.keys(CouponType).map((key) => (
                        {value: CouponType[key], label: this.label(`CouponType.${key}`)}
                    )), required: true, empty: '----'}},

                    {name: 'applied_to', type: 'select', props: {options: Object.keys(CouponAppliedTo).map((key) => (
                        {value: CouponAppliedTo[key], label: this.label(`CouponAppliedTo.${key}`)}
                    ))}, required: true},

                    {name: 'price_ranges', element: <PriceRangesField name="price_ranges" value={this.state.formData.price_ranges} onChange={this.onChange} />},

                    {name: 'shipping_method_id', type: 'queryselect', props: {
                        query: Queries.adminShippingMethods,
                        variables: {},
                        queryCallback: (result: QueryResult<AdminShippingMethodsList>) => {
                            return result.data!.adminShippingMethods.map((shippingMethod: AdminShippingMethodsList_adminShippingMethods) => (
                                {value: shippingMethod.id, label: shippingMethod.name}
                            ));
                        },
                        isClearable: true,
                        empty: '---',
                    }},

                    {name: 'payment_method_id', type: 'queryselect', props: {
                        query: Queries.adminPaymentMethods,
                        variables: {},
                        queryCallback: (result: QueryResult<AdminPaymentMethodsList>) => {
                            return result.data!.adminPaymentMethods.map((paymentMethod: AdminPaymentMethodsList_adminPaymentMethods) => (
                                {value: paymentMethod.id, label: paymentMethod.name}
                            ));
                        },
                        isClearable: true,
                        empty: '---',
                    }},

                    {name: 'usage_count', type: 'number', props: {required: true}},

                    {name: 'coupon_code_attributes', element: <CouponCodeAttributesForm name="coupon_code_attributes" value={this.state.formData.coupon_code_attributes} onChange={this.onChange} />},

                    {name: 'is_free_shipping', type: 'checkbox'},
                    {name: 'is_combinable', type: 'checkbox'},
                    {name: 'is_active', type: 'checkbox'},
                ],
            },
            {
                id: 'codes',
                fields: [
                    <CouponCodesField
                        key="codes"
                        name="codes"
                        codes={this.state.codes}
                        maxUsageCount={this.props.originalMaxUsageCount}
                        generatedCodesCount={this.state.generatedCodesCount}
                        onChange={this.onChange}
                    />,
                ],
            },
            {
                id: 'products',
                fields: [
                    <ProductInput key="products" name="products" products={this.state.products} onChange={this.onChange} />,
                ],
            },
        ];

        return (
            <Form
                intlPrefix={`module.coupon`}
                redirectPath={`${Paths.coupon}`}
                fields={fields}
                formData={this.state.formData}
                onSubmit={this.onSubmit}
                validate={this.validate}
                onChange={this.onFormChange}
            />
        );
    }
}

export default compose(
    injectIntl,
    withRouter,
)(CouponForm);



// WEBPACK FOOTER //
// ./src/modules/Coupon/components/CouponForm.tsx